import { forwardRef } from "react";
import { Link as RouterLink } from "react-router-dom";

type LinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: string;
  replace?: boolean;
  prefetch?: boolean;
  scroll?: boolean;
};

function scrollToHash(hash: string) {
  const id = decodeURIComponent(hash.replace(/^#/, ""));
  if (!id) return false;
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  return true;
}

const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, replace, prefetch: _prefetch, scroll: _scroll, onClick, ...props },
  ref,
) {
  if (
    href.startsWith("http://") ||
    href.startsWith("https://") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:")
  ) {
    return <a ref={ref} href={href} onClick={onClick} {...props} />;
  }

  // Same-page hash — React Router Link does not scroll to in-page anchors reliably
  if (href.startsWith("#")) {
    return (
      <a
        ref={ref}
        href={href}
        onClick={(e) => {
          onClick?.(e);
          if (e.defaultPrevented) return;
          if (scrollToHash(href)) {
            e.preventDefault();
            const path = `${window.location.pathname}${window.location.search}${href}`;
            window.history.pushState(null, "", path);
          }
        }}
        {...props}
      />
    );
  }

  return <RouterLink ref={ref} to={href} replace={replace} onClick={onClick} {...props} />;
});

export default Link;
